import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";

/** Public, unauthenticated read-only preview page. */
export default function SharePreview() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/ph360/share/${token}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `Link unavailable (${res.status})`);
        }
        const data = await res.json();
        setMeta(data);
        document.title = `${data.title} — Shared preview · BookForge360`;
      } catch (err) {
        setError(err.message || "Link unavailable");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div style={styles.wrap}>
        <div style={styles.errCard} data-testid="share-error">
          <p style={styles.eyebrow}>§ Share link</p>
          <h1 style={styles.errTitle}>This preview isn't available.</h1>
          <p style={styles.errBody}>{error}</p>
          <p style={styles.errBody}>The owner may have revoked or regenerated the link.</p>
        </div>
      </div>
    );
  }
  if (!meta) return <div style={styles.wrap}><p style={styles.loading}>Loading preview…</p></div>;

  return (
    <div style={styles.wrap} data-testid="share-preview-page">
      <header style={styles.header}>
        <p style={styles.eyebrow}>§ Shared read-only preview · PublishingHub360</p>
        <h1 style={styles.title} data-testid="share-title">{meta.title}</h1>
        {meta.subtitle && <p style={styles.subtitle}>{meta.subtitle}</p>}
        {meta.author && <p style={styles.author}>by {meta.author}</p>}
      </header>
      <div style={styles.frameShell}>
        <iframe
          data-testid="share-iframe"
          title="Shared preview"
          src={`${API}/ph360/share/${token}/preview`}
          style={styles.iframe}
        />
      </div>
      <footer style={styles.footer}>
        <span>Read-only preview · exports and edits require an account</span>
      </footer>
    </div>
  );
}

const styles = {
  wrap: { minHeight: "100vh", background: "#f6f4ee",
          fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
          color: "#1a1a18",
          display: "flex", flexDirection: "column" },
  header: { padding: "40px 32px 20px", maxWidth: 1000, width: "100%", margin: "0 auto" },
  eyebrow: { fontFamily: "'Inter', system-ui, sans-serif",
             textTransform: "uppercase", letterSpacing: "0.28em",
             fontSize: 11, color: "#7a7a72", margin: 0 },
  title: { fontSize: "clamp(36px, 6vw, 68px)", lineHeight: 0.98, margin: "16px 0 0",
           letterSpacing: "-0.01em" },
  subtitle: { fontSize: 22, color: "#5c5c58", margin: "10px 0 0", fontStyle: "italic" },
  author: { marginTop: 8, fontSize: 15,
            fontFamily: "'Inter', system-ui, sans-serif", color: "#5c5c58" },
  frameShell: { maxWidth: 1000, width: "100%", margin: "10px auto 24px",
                padding: "0 32px", flex: 1, display: "flex" },
  iframe: { width: "100%", flex: 1, minHeight: "70vh", border: "1px solid #d9d4c6",
            background: "#fff", boxShadow: "0 24px 60px -32px rgba(24, 24, 20, 0.35)" },
  footer: { padding: "18px 32px 28px", textAlign: "center",
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 12, color: "#7a7a72" },
  loading: { padding: 48, textAlign: "center",
             fontFamily: "'Inter', system-ui, sans-serif", color: "#7a7a72" },
  errCard: { maxWidth: 520, margin: "80px auto", padding: 32,
             background: "#fff", border: "1px solid #d9d4c6" },
  errTitle: { fontSize: 40, margin: "12px 0 8px", lineHeight: 1.05 },
  errBody: { fontFamily: "'Inter', system-ui, sans-serif",
             fontSize: 14, color: "#5c5c58", marginTop: 8 },
};
